import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { RiderService, AppDataService } from 'app/services';
import { DriverTypeEnum } from 'app/models';
import { Utils } from 'app/utils';
import * as moment from 'moment';

interface Driver {
	id: number;
	firstName: string;
	lastName: string;
	phoneNo: string;
	email: string;
}

enum MenuTab {
	Vehicle = 1,
	Driver = 2,
}

@Component({
	selector: 'taxi-vehicle-details',
	templateUrl: './taxi-vehicle-details.component.html',
	styleUrls: ['./taxi-vehicle-details.component.scss',
		'../../../../../styles/sidebar.scss',
		'../../../../../styles/style-layout.scss'
	]
})
export class TaxiVehicleDetailComponent implements OnInit {
	public vehicle: any;
	@Input()
	set _vehicle(value: any) {
		if (value != null) {
			this.vehicle = value;
			this.syncAssignedDriversFromVehicle();
		}
	}

	@Input()
	public isVisible: boolean = false;

	@Input()
	public vehiclePool: 'taxi' | 'connex' = 'taxi';

	@Output() changedVisibleTaxiVehicleDetailEmit = new EventEmitter<boolean>();

	@Output() vehicleAssignmentChanged = new EventEmitter<void>();

	changedVisible(increased: any) {
		this.changedVisibleTaxiVehicleDetailEmit.emit(increased);
	}

	public showSpinner: boolean = false;

	public menuTab = MenuTab;
	public activeMenuTab: MenuTab = MenuTab.Vehicle;

	public drivers: Driver[] = [];

	public showAssignDriverDialog = false;
	public connexDriverOptions: { label: string; value: number }[] = [];
	public selectedDriverIdForAssign: number | null = null;
	public loadingConnexDrivers = false;

	constructor(
		private messageService: MessageService,
		private riderService: RiderService,
		public appData: AppDataService
	) { }

	public menuItems: MenuItem[] = [
		{
			label: 'Vehicle',
			id: `${MenuTab.Vehicle}`,
			command: (e: any) => this.handleMenuTabChange(+e.item.id)
		},
		{
			label: 'Driver',
			id: `${MenuTab.Driver}`,
			command: (e: any) => this.handleMenuTabChange(+e.item.id)
		}
	];

	ngOnInit(): void {
	}

	private syncAssignedDriversFromVehicle(): void {
		this.drivers = [];
		if (!this.vehicle) {
			return;
		}
		const riderId = this.vehicle.AssignedRiderId ?? this.vehicle.assignedRiderId;
		if (!riderId) {
			return;
		}
		const first = this.vehicle.AssignedDriverFirstName ?? this.vehicle.assignedDriverFirstName ?? '';
		const last = this.vehicle.AssignedDriverLastName ?? this.vehicle.assignedDriverLastName ?? '';
		this.drivers = [{
			id: riderId,
			firstName: first,
			lastName: last,
			phoneNo: '',
			email: ''
		}];
	}

	public handleMenuTabChange(menuTab: MenuTab) {
		this.activeMenuTab = menuTab;
	}

	cancel() {
		this.changedVisible(false);
	}

	addDriver(): void {
		if (this.vehiclePool !== 'connex') {
			return;
		}
		this.selectedDriverIdForAssign = null;
		this.showAssignDriverDialog = true;
		this.loadConnexDriversForDropdown();
	}

	private loadConnexDriversForDropdown(): void {
		this.loadingConnexDrivers = true;
		const dateStart = moment().subtract(5, 'years');
		const dateEnd = moment().add(1, 'day');
		const dto = {
			ClientLocationType: this.appData.getClientLocationType(),
			OperatorId: this.appData.authData.OperatorId,
			SearchName: '',
			StartDateJoin: dateStart.format('YYYY-MM-DD'),
			EndDateJoin: dateEnd.format('YYYY-MM-DD'),
			Id: null,
			UserName: null,
			Phone: null,
			Vehicle: null,
			PlateNumber: null,
			Email: null,
			CountTrips: null,
			CompleteRate: null,
			AcceptanceRate: null,
			CancellationRate: null,
			LastActiveType: null,
			LastActiveAt: null,
			LastLogin: null,
			Status: null,
			Rating: null,
			ServiceTypes: [],
			VehicleTypes: [],
			RiderStatuses: null,
			SignupDate: null,
			Region: null,
			Service: null,
			DriverCampaign: null,
			ProbationExpired: null,
			ProbationExpiring: null,
			RiderSourceType: null,
			Filter: { ServiceType: null, VehicleType: null, SignupDate: null },
			Skip: 0,
			Take: 500,
			SortField: 'Id',
			SortOrder: -1,
			TimeZoneOffset: Utils.getTimezoneOffset(),
			RiderType: DriverTypeEnum.Connex
		};

		this.riderService.getAdminRiders(dto, this.messageService)
			.then(data => {
				this.loadingConnexDrivers = false;
				if (!data?.Success) {
					this.messageService.add({
						severity: 'error', summary: 'Error',
						detail: data?.ErrorMessage ?? 'Failed to load drivers'
					});
					this.connexDriverOptions = [];
					return;
				}
				const items = data.Items || [];
				this.connexDriverOptions = items
					.map((r: any) => {
						const raw = r.id ?? r.Id ?? (r.riderId > 0 ? r.riderId : null) ?? (r.RiderId > 0 ? r.RiderId : null);
						const rid = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
						return {
							label: (`${r.FirstName || r.firstName || ''} ${r.LastName || r.lastName || ''}`.trim() || `Rider #${rid}`).trim(),
							value: rid
						};
					})
					.filter((o: { label: string; value: number }) => !Number.isNaN(o.value) && o.value > 0);
			})
			.catch(() => {
				this.loadingConnexDrivers = false;
				this.connexDriverOptions = [];
			});
	}

	confirmAssignDriver(): void {
		if (!this.vehicle?.Id) {
			return;
		}
		if (this.selectedDriverIdForAssign == null) {
			this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Select a driver' });
			return;
		}
		this.riderService.assignVehicleToRider({
			VehicleId: this.vehicle.Id,
			DriverId: this.selectedDriverIdForAssign
		}, this.messageService)
			.then(data => {
				if (!data?.Success) {
					this.messageService.add({
						severity: 'error', summary: 'Error',
						detail: data?.ErrorMessage ?? 'Assignment failed'
					});
					return;
				}
				this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Driver assigned' });
				this.showAssignDriverDialog = false;
				this.vehicleAssignmentChanged.emit();
			});
	}

	removeDriver(_driverId: number): void {
		if (this.vehiclePool !== 'connex' || !this.vehicle?.Id) {
			return;
		}
		this.riderService.assignVehicleToRider({
			VehicleId: this.vehicle.Id,
			DriverId: null
		}, this.messageService)
			.then(data => {
				if (!data?.Success) {
					this.messageService.add({
						severity: 'error', summary: 'Error',
						detail: data?.ErrorMessage ?? 'Failed to remove driver'
					});
					return;
				}
				this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Driver removed' });
				this.vehicleAssignmentChanged.emit();
			});
	}
}
